import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { FieldError } from "@/components/FormFeedback";
import { formatDate } from "@/lib/date";
import { cn } from "@/lib/utils";

const PERSONAL_FIELDS = [["mobile", "Mobile"], ["dob", "DOB"], ["age", "Age"], ["gender", "Gender"], ["address", "Address"]];

const MEDICAL_FIELDS = [
  ["primary_diagnosis", "Primary Diagnosis"],
  ["diagnosis_date", "Diagnosis Date"],
  ["known_conditions", "Known Medical Conditions"],
  ["past_medical_history", "Past Medical History"],
  ["family_history", "Family History"],
  ["allergies", "Allergies"],
  ["height", "Height (cm)"],
  ["weight", "Weight (kg)"],
  ["blood_pressure", "Blood Pressure (mm Hg)"],
  ["previous_medicines", "Previous Medicines"],
  ["previous_prescriptions_note", "Previous Prescriptions Note"],
  ["previous_clinic_name", "Previous Clinic/Hospital Name"],
  ["previous_doctor_name", "Previous Doctor Name"],
  ["previous_treatment_location", "Previous Treatment Location"],
];
const MEDICAL_DATE_FIELDS = ["diagnosis_date"];

// The "Personal & Medical" tab of the Patient Profile — two editable cards
// (Personal Details, Medical History & Allergies). All state/handlers are
// owned by the parent PatientProfileContent and passed down as props.
export default function PatientPersonalMedicalTab({
  patient,
  form,
  setForm,
  editingPersonal,
  setEditingPersonal,
  editingMedical,
  setEditingMedical,
  editTouched,
  setEditTouched,
  editFieldErrors,
  editIsValid,
  setEditField,
  saveEdit,
  cancelEdit,
}) {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-testid="personal-details-card">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Personal Details</h2>
          {!editingPersonal ? (
            <Button data-testid="edit-patient-btn" size="sm" className="bg-teal-700 hover:bg-teal-800" onClick={() => setEditingPersonal(true)}>Edit</Button>
          ) : (
            <div className="flex gap-2">
              <Button data-testid="cancel-edit-btn" variant="outline" size="sm" onClick={() => { setEditingPersonal(false); cancelEdit(["mobile", "dob", "age", "gender", "address"]); setEditTouched({}); }}>Cancel</Button>
              <Button data-testid="save-edit-btn" size="sm" disabled={!editIsValid} className="bg-teal-700 hover:bg-teal-800" onClick={() => saveEdit("personal")}>Save</Button>
            </div>
          )}
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {PERSONAL_FIELDS.map(([f, label]) => (
            <div key={f}>
              <Label>{label}</Label>
              {editingPersonal ? (
                <>
                  {f === "dob" ? (
                    <DatePicker testId={`field-${f}`} value={form[f] || ""} onChange={(v) => setEditField(f, v)} toYear={new Date().getFullYear()} />
                  ) : (
                    <Input
                      data-testid={`field-${f}`}
                      value={form[f] || ""}
                      maxLength={f === "mobile" ? 10 : undefined}
                      onBlur={() => setEditTouched((t) => ({ ...t, [f]: true }))}
                      className={cn(editTouched[f] && editFieldErrors[f] && "border-red-500 focus-visible:ring-red-500")}
                      onChange={(e) => setEditField(f, f === "mobile" ? e.target.value.replace(/[^\d]/g, "").slice(0, 10) : e.target.value)}
                    />
                  )}
                  {editTouched[f] && <FieldError testId={`field-${f}-error`} message={editFieldErrors[f]} />}
                </>
              ) : (
                <p className="mt-1 text-sm text-slate-900">{f === "dob" ? formatDate(patient[f]) : patient[f] || "—"}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-testid="medical-history-card">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Medical History &amp; Allergies</h2>
          {!editingMedical ? (
            <Button data-testid="edit-medical-btn" size="sm" className="bg-teal-700 hover:bg-teal-800" onClick={() => setEditingMedical(true)}>Edit</Button>
          ) : (
            <div className="flex gap-2">
              <Button data-testid="cancel-medical-edit-btn" variant="outline" size="sm" onClick={() => { setEditingMedical(false); cancelEdit(MEDICAL_FIELDS.map(([f]) => f)); }}>Cancel</Button>
              <Button data-testid="save-medical-edit-btn" size="sm" className="bg-teal-700 hover:bg-teal-800" onClick={() => saveEdit("medical")}>Save</Button>
            </div>
          )}
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {MEDICAL_FIELDS.map(([f, label]) => (
            <div key={f}>
              <Label>{label}</Label>
              {editingMedical ? (
                MEDICAL_DATE_FIELDS.includes(f) ? (
                  <DatePicker testId={`field-${f}`} value={form[f] || ""} onChange={(v) => setForm((s) => ({ ...s, [f]: v }))} toYear={new Date().getFullYear()} />
                ) : (
                  <Textarea data-testid={`field-${f}`} value={form[f] || ""} onChange={(e) => setForm((s) => ({ ...s, [f]: e.target.value }))} rows={1} />
                )
              ) : (
                <p className="mt-1 text-sm text-slate-900">{MEDICAL_DATE_FIELDS.includes(f) ? formatDate(patient[f]) : patient[f] || "—"}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
